import { useQuery, useMutation, useQueryClient } from 'react-query';
import { categoryApi, subcategoryApi } from '../services/api';
import { useToast } from './useToast';
import type {
  CategoryCreate,
  CategoryUpdate,
  SubcategoryCreate,
  SubcategoryUpdate,
} from '../types';

// Category hooks
export function useCategories() {
  return useQuery(['categories'], () => categoryApi.getAll());
}

export function useCategory(categoryId: string) {
  return useQuery(['categories', categoryId], () => categoryApi.getById(categoryId), {
    enabled: !!categoryId,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((data: CategoryCreate) => categoryApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      showSuccess('Categoría creada exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear la categoría');
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ categoryId, data }: { categoryId: string; data: CategoryUpdate }) =>
      categoryApi.update(categoryId, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['categories', variables.categoryId]);
        showSuccess('Categoría actualizada exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar la categoría');
      },
    }
  );
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((categoryId: string) => categoryApi.delete(categoryId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      showSuccess('Categoría eliminada exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al eliminar la categoría');
    },
  });
}

// Subcategory hooks
export function useSubcategories(categoryId: string) {
  return useQuery(
    ['subcategories', categoryId],
    () => subcategoryApi.getByCategory(categoryId),
    {
      enabled: !!categoryId,
    }
  );
}

export function useSubcategory(subcategoryId: string) {
  return useQuery(
    ['subcategories', 'detail', subcategoryId],
    () => subcategoryApi.getById(subcategoryId),
    {
      enabled: !!subcategoryId,
    }
  );
}

export function useCreateSubcategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation((data: SubcategoryCreate) => subcategoryApi.create(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['categories']);
      queryClient.invalidateQueries(['subcategories', data.categoryId]);
      showSuccess('Subcategoría creada exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear la subcategoría');
    },
  });
}

export function useUpdateSubcategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ subcategoryId, data }: { subcategoryId: string; data: SubcategoryUpdate }) =>
      subcategoryApi.update(subcategoryId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['subcategories', data.categoryId]);
        queryClient.invalidateQueries(['subcategories', 'detail', data.subcategoryId]);
        showSuccess('Subcategoría actualizada exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar la subcategoría');
      },
    }
  );
}

export function useDeleteSubcategory() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  
  return useMutation(
    ({ subcategoryId }: { subcategoryId: string; categoryId: string }) =>
      subcategoryApi.delete(subcategoryId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['subcategories', variables.categoryId]);
        showSuccess('Subcategoría eliminada exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al eliminar la subcategoría');
      },
    }
  );
}
